import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import PawLogo from "../ui/PawLogo";

function useIsDesktop(breakpointPx = 900) {
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(`(min-width:${breakpointPx}px)`).matches
            : true
    );

    useEffect(() => {
        const mq = window.matchMedia(`(min-width:${breakpointPx}px)`);
        const handler = (e) => setIsDesktop(e.matches);

        mq.addEventListener?.("change", handler);
        mq.addListener?.(handler);

        setIsDesktop(mq.matches);

        return () => {
            mq.removeEventListener?.("change", handler);
            mq.removeListener?.(handler);
        };
    }, [breakpointPx]);

    return isDesktop;
}

export default function Footer({
                                   kennelName,
                                   primaryColor,
                                   navItems = [],
                                   onNavClick,
                                   contact = {
                                       address: "Lange Str. 35, 26434 Wangerland OT Hooksiel",
                                       phone: "+49 175 3203089",
                                       email: "Havaneser@vom-schnuesterhus.de",
                                   },
                                   legalLinks = { impressum: "#", datenschutz: "#" },

                                   // ✅ Fix: echte Social-Links als Default
                                   socialLinks = {
                                       instagram:
                                           "https://www.instagram.com/havaneser_vom_schnuesterhus/?utm_source=qr&igsh=OTUzcThtOXBqbDZl#",
                                       facebook:
                                           "https://www.facebook.com/people/Havaneser-vom-Schn%C3%BCsterhus/61584841313529/?rdid=fHzDfYdmIBwFC55q&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F14UnwnoskZo%2F",
                                   },

                                   styles,

                                   /** ✅ NEU (für zentrale Steuerung aus HomePage) */
                                   legalOpen: legalOpenProp, // "impressum" | "datenschutz" | null
                                   onOpenLegal, // (type) => void
                                   onCloseLegal, // () => void
                               }) {
    const isDesktop = useIsDesktop(900);

    const phoneHref = `tel:${(contact.phone || "").replace(/\s+/g, "")}`;
    const mailHref = `mailto:${contact.email || ""}`;

    // ✅ WhatsApp
    const whatsappNumber = contact.phone || "+49 175 3203089";
    const waHref = `https://wa.me/${whatsappNumber.replace(/[^\d]/g, "")}`;

    /**
     * ✅ Wenn Props vorhanden sind -> "controlled"
     * ✅ Wenn nicht -> fallback auf internen State (damit Footer auch standalone läuft)
     */
    const [legalOpenLocal, setLegalOpenLocal] = useState(null);
    const legalOpen = legalOpenProp !== undefined ? legalOpenProp : legalOpenLocal;

    const openLegal = (type, e) => {
        e?.preventDefault();
        if (typeof onOpenLegal === "function") onOpenLegal(type);
        else setLegalOpenLocal(type);
    };

    const closeLegal = () => {
        if (typeof onCloseLegal === "function") onCloseLegal();
        else setLegalOpenLocal(null);
    };

    const footerWrap = {
        maxWidth: "1200px",
        margin: "0 auto",
        position: "relative",
        zIndex: 2,
        padding: isDesktop ? "0 18px" : "0 16px",
    };

    const grid = {
        display: "grid",
        gridTemplateColumns: isDesktop ? "1.2fr 1fr 1fr" : "1fr",
        gap: isDesktop ? "40px" : "26px",
        alignItems: "start",
    };

    const title = {
        fontFamily: "'Playfair Display', serif",
        margin: "0 0 12px",
        fontSize: "1.1rem",
        letterSpacing: "0.2px",
    };

    const textMuted = { opacity: 0.9, lineHeight: 1.7, margin: 0 };

    const linkStyle = {
        cursor: "pointer",
        opacity: 0.9,
        marginBottom: "10px",
        transition: "all 0.2s ease",
    };

    const iconRow = {
        display: "flex",
        gap: 10,
        marginTop: 14,
        justifyContent: isDesktop ? "flex-start" : "flex-start",
        flexWrap: "wrap",
    };

    const iconBtn = {
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        textDecoration: "none",
        border: "1px solid rgba(255,255,255,0.14)",
    };

    const contactRow = {
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        marginBottom: 10,
        opacity: 0.95,
        lineHeight: 1.6,
    };

    const bottomBar = {
        marginTop: isDesktop ? 35 : 22,
        paddingTop: 18,
        borderTop: "1px solid rgba(255,255,255,0.18)",
        display: "flex",
        flexDirection: isDesktop ? "row" : "column",
        justifyContent: "space-between",
        alignItems: isDesktop ? "center" : "flex-start",
        gap: 12,
        flexWrap: "wrap",
        fontSize: 14,
        opacity: 0.9,
    };

    const navListStyle = {
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "grid",
        gridTemplateColumns: isDesktop ? "1fr" : "1fr 1fr",
        gap: isDesktop ? "0" : "8px 14px",
    };

    // ✅ Mini-Credits unter dem Footer
    const creditsBar = {
        marginTop: 14,
        paddingTop: 12,
        borderTop: "1px solid rgba(255,255,255,0.14)",
        fontSize: 12.5,
        opacity: 0.92,
        display: "flex",
        flexDirection: isDesktop ? "row" : "column",
        gap: 8,
        justifyContent: "space-between",
        alignItems: isDesktop ? "center" : "flex-start",
        flexWrap: "wrap",
    };

    const creditLink = {
        color: "#fff",
        textDecoration: "none",
        borderBottom: "1px solid rgba(255,255,255,0.35)",
    };

    // ✅ Footer-Navigation: Würfe nach "Hündinnen & Rüden" einfügen
    const footerNavItems = useMemo(() => {
        const littersKey = "litters";
        const littersLabel = "Würfe";

        // Entferne puppies/litter-a aus der Hauptliste (A-Wurf kommt unter Würfe)
        const base = (navItems || []).filter(
            (i) => i.key !== "puppies" && i.key !== "litter-a"
        );

        // A-Wurf: bevorzugt Route-Item "litter-a", sonst Fallback "puppies"
        const aWurfItem =
            (navItems || []).find((i) => i.key === "litter-a") ||
            (navItems || []).find((i) => i.key === "puppies");

        // "Hündinnen & Rüden" finden (Key ODER Label)
        const possibleDogsKeys = ["dogs", "breeding", "hunde", "females", "males"];
        let idx = base.findIndex((i) => possibleDogsKeys.includes(i.key));
        if (idx === -1) idx = base.findIndex((i) => /Hündinnen\s*&\s*Rüden/i.test(i.label));

        // Wenn "Würfe" schon existiert, nichts doppeln
        if (base.some((i) => i.key === littersKey)) return base;

        const littersItem = {
            key: littersKey,
            label: littersLabel,
            ref: aWurfItem?.ref ?? "#",
        };

        // Einfügen: nach Hündinnen&Rüden, sonst ans Ende
        const insertAt = idx !== -1 ? idx + 1 : base.length;
        return [...base.slice(0, insertAt), littersItem, ...base.slice(insertAt)];
    }, [navItems]);

    return (
        <footer
            style={{
                ...(styles?.footer || {}),
                backgroundColor: primaryColor,
                position: "relative",
            }}
        >
            {/* dezenter Hintergrund-Gradient */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(900px 450px at 20% 10%, rgba(255,255,255,0.14), transparent 55%), radial-gradient(700px 350px at 80% 0%, rgba(255,255,255,0.10), transparent 60%)",
                    zIndex: 1,
                    pointerEvents: "none",
                }}
            />

            <div style={footerWrap}>
                {/* TOP GRID */}
                <div style={grid}>
                    {/* Brand */}
                    <div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 12,
                            }}
                        >
                            <PawLogo color="#fff" size={32} />
                            <div>
                                <h3 style={{ ...title, margin: 0, fontSize: "1.25rem" }}>
                                    {kennelName}
                                </h3>
                                <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
                                    Liebevolle Havaneser Zucht
                                </p>
                            </div>
                        </div>

                        <p style={{ ...textMuted, maxWidth: 420 }}>
                            Wir züchten mit Herz, Verstand und Verantwortung – mit Fokus auf
                            Gesundheit, Wesen und optimale Sozialisierung.
                        </p>

                        {/* Social */}
                        <div style={iconRow}>
                            <motion.a
                                href={socialLinks.instagram}
                                target="_blank"
                                rel="noreferrer"
                                style={iconBtn}
                                whileHover={{ scale: 1.06 }}
                                whileTap={{ scale: 0.96 }}
                                aria-label="Instagram"
                                title="Instagram"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="2" />
                                    <path d="M16.5 7.5h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <path
                                        d="M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    />
                                </svg>
                            </motion.a>

                            <motion.a
                                href={socialLinks.facebook}
                                target="_blank"
                                rel="noreferrer"
                                style={iconBtn}
                                whileHover={{ scale: 1.06 }}
                                whileTap={{ scale: 0.96 }}
                                aria-label="Facebook"
                                title="Facebook"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M14 8h2V5h-2c-2.2 0-4 1.8-4 4v2H8v3h2v7h3v-7h2.2l.8-3H13V9c0-.6.4-1 1-1Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </motion.a>

                            {/* ✅ WhatsApp */}
                            <motion.a
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={iconBtn}
                                whileHover={{ scale: 1.06 }}
                                whileTap={{ scale: 0.96 }}
                                aria-label="WhatsApp"
                                title={`WhatsApp: ${whatsappNumber}`}
                            >
                                <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                                    <path
                                        d="M16 3C9.372 3 4 8.115 4 14.425c0 2.454.82 4.73 2.213 6.59L5 29l8.17-1.98a12.6 12.6 0 0 0 2.83.32c6.628 0 12-5.115 12-11.425C28 8.115 22.628 3 16 3Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M12.6 10.6c-.33-.75-.68-.77-.99-.78-.26-.01-.56-.01-.86-.01-.3 0-.79.11-1.2.55-.41.44-1.57 1.49-1.57 3.63 0 2.14 1.61 4.2 1.84 4.49.23.29 3.11 4.83 7.67 6.58 3.79 1.46 4.57 1.17 5.39 1.09.82-.08 2.63-1.05 3-2.07.37-1.02.37-1.89.26-2.07-.11-.18-.41-.29-.86-.5-.45-.21-2.63-1.24-3.03-1.38-.4-.14-.69-.21-.99.29-.3.5-1.14 1.38-1.4 1.66-.26.29-.52.32-.97.11-.45-.21-1.9-.68-3.62-2.17-1.34-1.14-2.24-2.55-2.5-2.98-.26-.43-.03-.66.2-.87.2-.19.45-.5.68-.75.23-.25.3-.43.45-.72.15-.29.08-.54-.04-.75-.12-.21-1.04-2.55-1.43-3.42Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </motion.a>
                        </div>
                    </div>

                    {/* Links */}
                    <div>
                        <h4 style={title}>Navigation</h4>
                        <ul style={navListStyle}>
                            {footerNavItems.map((item) => (
                                <motion.li
                                    key={item.key}
                                    style={{
                                        ...linkStyle,
                                        marginBottom: isDesktop ? "10px" : 0,
                                        padding: isDesktop ? 0 : "8px 10px",
                                        borderRadius: isDesktop ? 0 : 12,
                                        background: isDesktop ? "transparent" : "rgba(255,255,255,0.10)",
                                        border: isDesktop ? "none" : "1px solid rgba(255,255,255,0.12)",
                                    }}
                                    whileHover={isDesktop ? { x: 4, opacity: 1 } : { opacity: 1 }}
                                    whileTap={!isDesktop ? { scale: 0.98 } : { scale: 1 }}
                                    onClick={() => onNavClick?.(item.ref, item.key)}
                                >
                                    {item.label}
                                </motion.li>
                            ))}
                        </ul>
                    </div>

                    {/* Kontakt */}
                    <div>
                        <h4 style={title}>Kontakt</h4>

                        <div style={contactRow}>
              <span style={{ marginTop: 2, opacity: 0.9 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                      d="M12 13c1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3 1.3 3 3 3Z"
                      stroke="currentColor"
                      strokeWidth="2"
                  />
                  <path
                      d="M12 21c4-4 8-7.6 8-11 0-4.4-3.6-8-8-8S4 5.6 4 10c0 3.4 4 7 8 11Z"
                      stroke="currentColor"
                      strokeWidth="2"
                  />
                </svg>
              </span>
                            <span>{contact.address}</span>
                        </div>

                        <div style={contactRow}>
              <span style={{ marginTop: 2, opacity: 0.9 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                      d="M22 16.9V21a2 2 0 0 1-2.2 2 20 20 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A20 20 0 0 1 2 4.2 2 2 0 0 1 4 2h4.1a2 2 0 0 1 2 1.7c.1.8.3 1.6.5 2.4a2 2 0 0 1-.5 2.1L8.1 10.1a16 16 0 0 0 6 6l1.9-2.1a2 2 0 0 1 2.1-.5c.8.2 1.6.4 2.4.5a2 2 0 0 1 1.5 1.9Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                  />
                </svg>
              </span>
                            <a
                                href={phoneHref}
                                style={{
                                    color: "#fff",
                                    textDecoration: "none",
                                    borderBottom: "1px solid rgba(255,255,255,0.35)",
                                }}
                            >
                                {contact.phone}
                            </a>
                        </div>

                        <div style={contactRow}>
              <span style={{ marginTop: 2, opacity: 0.9 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                  />
                  <path d="M22 6 12 13 2 6" stroke="currentColor" strokeWidth="2" />
                </svg>
              </span>
                            <a
                                href={mailHref}
                                style={{
                                    color: "#fff",
                                    textDecoration: "none",
                                    borderBottom: "1px solid rgba(255,255,255,0.35)",
                                }}
                            >
                                {contact.email}
                            </a>
                        </div>

                        {/* ✅ WhatsApp als zusätzlicher Kontakt-Link */}
                        <div style={contactRow}>
              <span style={{ marginTop: 2, opacity: 0.9 }}>
                <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                  <path
                      d="M16 3C9.372 3 4 8.115 4 14.425c0 2.454.82 4.73 2.213 6.59L5 29l8.17-1.98a12.6 12.6 0 0 0 2.83.32c6.628 0 12-5.115 12-11.425C28 8.115 22.628 3 16 3Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                  />
                  <path
                      d="M12.6 10.6c-.33-.75-.68-.77-.99-.78-.26-.01-.56-.01-.86-.01-.3 0-.79.11-1.2.55-.41.44-1.57 1.49-1.57 3.63 0 2.14 1.61 4.2 1.84 4.49.23.29 3.11 4.83 7.67 6.58 3.79 1.46 4.57 1.17 5.39 1.09.82-.08 2.63-1.05 3-2.07.37-1.02.37-1.89.26-2.07-.11-.18-.41-.29-.86-.5-.45-.21-2.63-1.24-3.03-1.38-.4-.14-.69-.21-.99.29-.3.5-1.14 1.38-1.4 1.66-.26.29-.52.32-.97.11-.45-.21-1.9-.68-3.62-2.17-1.34-1.14-2.24-2.55-2.5-2.98-.26-.43-.03-.66.2-.87.2-.19.45-.5.68-.75.23-.25.3-.43.45-.72.15-.29.08-.54-.04-.75-.12-.21-1.04-2.55-1.43-3.42Z"
                      fill="currentColor"
                  />
                </svg>
              </span>

                            <a
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: "#fff",
                                    textDecoration: "none",
                                    borderBottom: "1px solid rgba(255,255,255,0.35)",
                                }}
                            >
                                WhatsApp: {contact.phone}
                            </a>
                        </div>
                    </div>
                </div>

                {/* SUBTILE PAW BACKGROUND */}
                <motion.div
                    style={{
                        position: "absolute",
                        top: 10,
                        left: 0,
                        right: 0,
                        display: "flex",
                        justifyContent: "space-around",
                        zIndex: 1,
                        pointerEvents: "none",
                        opacity: 0.18,
                    }}
                    animate={{ x: [0, 20, 0] }}
                    transition={{ repeat: Infinity, duration: 22, ease: "linear" }}
                >
                    {[...Array(isDesktop ? 5 : 3)].map((_, i) => (
                        <PawLogo key={i} color="rgba(255,255,255,0.20)" size={26} />
                    ))}
                </motion.div>

                {/* BOTTOM BAR */}
                <div style={bottomBar}>
                    <div>
                        &copy; {new Date().getFullYear()} {kennelName}. Alle Rechte vorbehalten.
                    </div>

                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                        <a
                            href={legalLinks.impressum}
                            onClick={(e) => openLegal("impressum", e)}
                            style={{ color: "#fff", textDecoration: "none", opacity: 0.9 }}
                        >
                            Impressum
                        </a>
                        <a
                            href={legalLinks.datenschutz}
                            onClick={(e) => openLegal("datenschutz", e)}
                            style={{ color: "#fff", textDecoration: "none", opacity: 0.9 }}
                        >
                            Datenschutz
                        </a>
                    </div>
                </div>

                {/* ✅ Mini-Credits unter dem Footer */}
                <div style={creditsBar}>
                    <div>
                        Fotos größtenteils von{" "}
                        <a
                            href="https://www.eilts-online.de/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={creditLink}
                        >
                            eilts-online.de
                        </a>
                    </div>

                    <div>
                        Webseite entwickelt von{" "}
                        <a
                            href="https://johannsen-its.de/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={creditLink}
                        >
                            johannsen-its.de
                        </a>
                    </div>
                </div>
            </div>

            {/* ✅ Legal Modal */}
            <LegalModal
                open={legalOpen}
                onClose={closeLegal}
                primaryColor={primaryColor}
                kennelName={kennelName}
                contact={contact}
            />
        </footer>
    );
}

/** =========================
 *  ✅ Legal Modal Component
 *  ========================= */
function LegalModal({ open, onClose, primaryColor, kennelName, contact }) {
    useEffect(() => {
        if (!open) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        document.addEventListener("keydown", onKeyDown);

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    const content = getLegalContent({ open, kennelName, contact });

    const backdrop = {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "18px",
    };

    const modal = {
        width: "min(860px, 100%)",
        maxHeight: "min(82vh, 900px)",
        overflow: "auto",
        background: "#fff",
        color: "#111",
        borderRadius: 18,
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        border: "1px solid rgba(0,0,0,0.06)",
    };

    const header = {
        position: "sticky",
        top: 0,
        background: "linear-gradient(180deg, #fff, rgba(255,255,255,0.92))",
        backdropFilter: "blur(6px)",
        padding: "16px 18px",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        zIndex: 2,
    };

    const badge = {
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 999,
        background: `${primaryColor}22`,
        color: primaryColor,
        border: `1px solid ${primaryColor}44`,
        fontWeight: 600,
    };

    const closeBtn = {
        border: "none",
        background: "rgba(0,0,0,0.06)",
        cursor: "pointer",
        borderRadius: 12,
        padding: "10px 12px",
        fontSize: 14,
    };

    const body = {
        padding: "16px 18px 20px",
        lineHeight: 1.65,
        fontSize: 14.5,
    };

    const h2 = {
        margin: 0,
        fontFamily: "'Playfair Display', serif",
        fontSize: 20,
    };

    const h3 = { margin: "16px 0 8px", fontSize: 15.5 };
    const p = { margin: "0 0 10px" };
    const ul = { margin: "0 0 10px 18px" };

    return (
        <motion.div
            style={backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
            role="dialog"
            aria-modal="true"
            aria-label={content.title}
        >
            <motion.div
                style={modal}
                initial={{ y: 18, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 18, opacity: 0, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
            >
                <div style={header}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <h2 style={h2}>{content.title}</h2>
                            <span style={badge}>{kennelName}</span>
                        </div>
                        {content.subtitle ? (
                            <div style={{ fontSize: 13, opacity: 0.75 }}>{content.subtitle}</div>
                        ) : null}
                    </div>

                    <button type="button" onClick={onClose} style={closeBtn} aria-label="Modal schließen">
                        ✕ Schließen
                    </button>
                </div>

                <div style={body}>
                    {content.blocks.map((b, idx) => {
                        if (b.type === "h3") return <h3 key={idx} style={h3}>{b.text}</h3>;
                        if (b.type === "p") return <p key={idx} style={p}>{b.text}</p>;
                        if (b.type === "ul")
                            return (
                                <ul key={idx} style={ul}>
                                    {b.items.map((it, i) => <li key={i}>{it}</li>)}
                                </ul>
                            );
                        return null;
                    })}
                </div>
            </motion.div>
        </motion.div>
    );
}

function getLegalContent({ open, kennelName, contact }) {
    if (open === "impressum") {
        return {
            title: "Impressum",
            subtitle: "Angaben gemäß § 5 TMG (bitte prüfen & ergänzen)",
            blocks: [
                { type: "h3", text: "Anbieter" },
                { type: "p", text: `${kennelName}` },
                { type: "p", text: contact?.address || "Adresse ergänzen" },

                { type: "h3", text: "Kontakt" },
                {
                    type: "ul",
                    items: [`Telefon: ${contact?.phone || "—"}`, `E-Mail: ${contact?.email || "—"}`],
                },

                { type: "h3", text: "Verantwortlich für den Inhalt" },
                { type: "p", text: "Claudia Don" },

                { type: "h3", text: "Haftung für Inhalte" },
                {
                    type: "p",
                    text:
                        "Wir sind für eigene Inhalte nach den allgemeinen Gesetzen verantwortlich. Für Inhalte externer Links übernehmen wir keine Gewähr; verantwortlich ist stets der jeweilige Anbieter.",
                },

                { type: "h3", text: "Urheberrecht" },
                {
                    type: "p",
                    text:
                        "Die Inhalte und Werke auf dieser Seite unterliegen dem Urheberrecht. Eine Vervielfältigung, Bearbeitung oder Verbreitung bedarf der schriftlichen Zustimmung, sofern nicht gesetzlich erlaubt.",
                },
            ],
        };
    }

    return {
        title: "Datenschutzerklärung",
        subtitle: "Informationen nach DSGVO",
        blocks: [
            { type: "h3", text: "1. Verantwortlicher" },
            { type: "p", text: `${kennelName}` },
            { type: "p", text: contact?.address || "Adresse ergänzen" },
            {
                type: "ul",
                items: [`E-Mail: ${contact?.email || "—"}`, `Telefon: ${contact?.phone || "—"}`],
            },

            { type: "h3", text: "2. Allgemeine Hinweise" },
            {
                type: "p",
                text:
                    "Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung dieser Website erforderlich ist oder Sie uns Daten aktiv mitteilen (z. B. per E-Mail).",
            },

            { type: "h3", text: "3. Hosting & Server-Logfiles" },
            {
                type: "p",
                text:
                    "Beim Aufruf der Website können technisch notwendige Daten (z. B. IP-Adresse, Zeitpunkt, Browser) in Server-Logfiles verarbeitet werden. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem Betrieb).",
            },

            { type: "h3", text: "4. Kontaktaufnahme" },
            {
                type: "p",
                text:
                    "Wenn Sie uns kontaktieren (z. B. per E-Mail), verarbeiten wir Ihre Angaben zur Bearbeitung der Anfrage. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (vorvertraglich/Vertrag) bzw. Art. 6 Abs. 1 lit. f DSGVO. Das Kontaktformular verwendet Formspree als Zwischenlieferant der Anfrage.",
            },

            { type: "h3", text: "5. Externe Links / Social Media" },
            {
                type: "p",
                text:
                    "Unsere Website kann Links zu externen Anbietern (z. B. Instagram/Facebook) enthalten. Beim Anklicken verlassen Sie unsere Seite; für die Datenverarbeitung dort ist der jeweilige Anbieter verantwortlich.",
            },

            { type: "h3", text: "6. Ihre Rechte" },
            {
                type: "ul",
                items: [
                    "Auskunft (Art. 15 DSGVO)",
                    "Berichtigung (Art. 16 DSGVO)",
                    "Löschung (Art. 17 DSGVO)",
                    "Einschränkung (Art. 18 DSGVO)",
                    "Datenübertragbarkeit (Art. 20 DSGVO)",
                    "Widerspruch (Art. 21 DSGVO)",
                    "Beschwerde bei einer Aufsichtsbehörde",
                ],
            },

            { type: "h3", text: "7. Stand" },
            { type: "p", text: `Stand: ${new Date().toLocaleDateString("de-DE")}` },
        ],
    };
}
